import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useSignOutExternal } from 'components/App/hooks'

import { confirm } from 'components/Shared/ConfirmDialog/Index'
import { RouteProfile, RouteSubscriptionsViews, RouteSubscriptionsTickets } from 'routes'

import AvatarImage from 'assets/img/avatar.svg'

import * as sessionUserSelector from 'selectors/sessionUser'
import * as ticketingSystemsSelector from 'selectors/ticketingSystems'

import NavBarMenu, { StyledNavBarMenuTitle, StyledNavBarMenuContent } from './NavBarMenu/Index'
import NavBarMenuItem, { NavBarMenuItemStyles } from './NavBarMenu/NavBarMenuItem'

const StyledNavUser = styled.div`
  ${StyledNavBarMenuTitle} {
    padding-left: 12px;
    padding-right: 16px;
  }

  ${StyledNavBarMenuContent} {
    right: 0;
  }
`

const StyledNavUserTitle = styled.div`
  display: flex;
  align-items: center;
  height: 48px;
`

const UserAvatar = styled.img`
  height: 24px;
  width: 24px;
  background-size: 100%;
  border-radius: 50%;
  margin-right: 4px;
`

const UserName = styled.div`
  font-size: 10px;
`

const StyledNavBarSignOutMenuItem = styled.div`
  ${NavBarMenuItemStyles}

  &:focus {
    outline: none;
  }
`

const NavUserTitle = ({ userAvatarUrl, userName }) => {
  return (
    <StyledNavUserTitle>
      <UserAvatar src={userAvatarUrl || AvatarImage} />
      <UserName data-test='nav-user-name'>{userName}</UserName>
    </StyledNavUserTitle>
  )
}

NavUserTitle.propTypes = {
  userAvatarUrl: PropTypes.string,
  userName: PropTypes.string,
}

const NavBarSignOutMenuItem = ({ name, signOut, innerRef }) => {
  return (
    <StyledNavBarSignOutMenuItem
      onClick={() => signOut()}
      onKeyPress={(e) => e.key === 'Enter' && signOut()}
      onMouseEnter={(e) => e.currentTarget.focus()}
      ref={innerRef}
      tabIndex={0}
    >
      {name}
    </StyledNavBarSignOutMenuItem>
  )
}

NavBarSignOutMenuItem.propTypes = {
  name: PropTypes.string,
  signOut: PropTypes.func.isRequired,
  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
}

const NavUser = () => {
  const location = useLocation()
  const { t } = useTranslation()
  const { signOutExternal } = useSignOutExternal()
  const userDisplayName = useSelector(sessionUserSelector.displayName)
  const userAvatarUrl = useSelector(sessionUserSelector.avatarUrl)
  const ticketingSystemsNotEmpty = useSelector(ticketingSystemsSelector.ticketingSystemsNotEmpty)
  const ticketingSystems = useSelector(ticketingSystemsSelector.ticketingSystems)
  const firstTicketingSystemDisplayId = ticketingSystems[0]?.displayId
  const userIsAdmin = useSelector(sessionUserSelector.isAdmin)
  const userIsPowerUser = useSelector(sessionUserSelector.isPowerUser)
  const isShowUserSubscriptions = userIsAdmin || userIsPowerUser

  const signOut = () => {
    confirm({
      title: t('mainHeader.userSignOutTitle'),
      message: t('mainHeader.userSignOutConfirm'),
      t,
    }).then(
      () => { signOutExternal() },
      () => {}
    )
  }

  const matchProfilePath = RouteProfile.matchUrlExact(location.pathname)
  const matchSubscriptionsViewsPath = RouteSubscriptionsViews.matchUrlExact(location.pathname)
  const matchSubscriptionsTicketsPath = RouteSubscriptionsTickets.matchUrlExact(location.pathname)
  const activeNavTab = !!(matchProfilePath || matchSubscriptionsViewsPath || matchSubscriptionsTicketsPath)

  return (
    <StyledNavUser>
      <NavBarMenu
        active={activeNavTab}
        title={
          <NavUserTitle userAvatarUrl={userAvatarUrl} userName={userDisplayName} />
        }
      >
        <NavBarMenuItem name={t('mainHeader.userProfile')} to={RouteProfile()} />
        {(ticketingSystemsNotEmpty && !isShowUserSubscriptions) && (
          <NavBarMenuItem name={t('mainHeader.userSubscriptions')} to={RouteSubscriptionsViews({ ticketingSystemDisplayId: firstTicketingSystemDisplayId })} />
        )}
        <NavBarSignOutMenuItem name={t('mainHeader.userSignOut')} signOut={signOut} />
      </NavBarMenu>
    </StyledNavUser>
  )
}

export default NavUser
